import React, { useMemo } from 'react';
import { Box, Flex, Group, Text, Tooltip } from '@mantine/core';
import dayjs from 'dayjs';

type ReviewSource =
    | 'DASHBOARD_DWELL'
    | 'EXPLICIT_CONFIRM'
    | 'EXPLICIT_CORRECTION'
    | null
    | undefined;

type ActivityEntry = {
    date: string;
    reviewed: boolean;
    firstReviewedAt?: string | null;
    firstReviewedSource?: ReviewSource;
};

type Props = {
    activity: ActivityEntry[];
};

const sourceLabel = (source: ReviewSource): string | null => {
    if (source === 'EXPLICIT_CONFIRM') return 'confirmed';
    if (source === 'EXPLICIT_CORRECTION') return 'captured corrections';
    if (source === 'DASHBOARD_DWELL') return 'viewed via dashboard';
    return null;
};

function ActivityGrid({ activity }: Props) {
    const days = useMemo(() => {
        const today = dayjs().startOf('day');
        const grid: {
            date: dayjs.Dayjs;
            reviewed: boolean | null;
            firstReviewedAt?: string | null;
            firstReviewedSource?: ReviewSource;
        }[] = [];

        for (let i = 29; i >= 0; i -= 1) {
            const date = today.subtract(i, 'day');
            const entry = activity.find(
                a =>
                    dayjs(a.date).format('YYYY-MM-DD') ===
                    date.format('YYYY-MM-DD'),
            );
            grid.push({
                date,
                reviewed: entry ? entry.reviewed : null,
                firstReviewedAt: entry?.firstReviewedAt,
                firstReviewedSource: entry?.firstReviewedSource,
            });
        }
        return grid;
    }, [activity]);

    return (
        <Box>
            <Flex gap={6} wrap="wrap">
                {days.map(day => {
                    const dateKey = day.date.format('YYYY-MM-DD');
                    const isToday = day.date.isSame(dayjs(), 'day');
                    let bgColor = '#E5E7EB';
                    let label = 'No data';
                    if (day.reviewed === true) {
                        bgColor = '#22C55E';
                        const sourceText = sourceLabel(day.firstReviewedSource);
                        const timeText = day.firstReviewedAt
                            ? ` at ${dayjs(day.firstReviewedAt).format(
                                  'HH:mm',
                              )}`
                            : '';
                        label = sourceText
                            ? `Reviewed (${sourceText})${timeText}`
                            : `Reviewed${timeText}`;
                    } else if (day.reviewed === false) {
                        bgColor = '#EF4444';
                        label = 'Missed';
                    }

                    return (
                        <Tooltip
                            key={dateKey}
                            label={`${day.date.format(
                                'ddd, DD MMM',
                            )} — ${label}`}
                            withArrow
                            position="top"
                            openDelay={100}
                        >
                            <Box
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 8,
                                    backgroundColor: bgColor,
                                    border: isToday
                                        ? '2.5px solid #004D94'
                                        : '1px solid rgba(0,0,0,0.04)',
                                    cursor: 'default',
                                    transition:
                                        'transform 0.15s ease, box-shadow 0.15s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                }}
                                onMouseEnter={e => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.transform = 'scale(1.15)';
                                    el.style.boxShadow =
                                        '0 2px 8px rgba(0,0,0,0.15)';
                                    el.style.zIndex = '1';
                                }}
                                onMouseLeave={e => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.transform = 'scale(1)';
                                    el.style.boxShadow = 'none';
                                    el.style.zIndex = '0';
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 8,
                                        lineHeight: 1,
                                        letterSpacing: 0.3,
                                    }}
                                    c={
                                        day.reviewed === null
                                            ? 'dimmed'
                                            : 'rgba(255,255,255,0.7)'
                                    }
                                    fw={500}
                                    tt="uppercase"
                                >
                                    {day.date.format('MMM')}
                                </Text>
                                <Text
                                    fw={isToday ? 700 : 500}
                                    c={
                                        day.reviewed === null
                                            ? 'dimmed'
                                            : 'white'
                                    }
                                    style={{ fontSize: 13, lineHeight: 1 }}
                                >
                                    {day.date.format('D')}
                                </Text>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Flex>

            <Group gap="xl" mt="lg">
                <Group gap={8}>
                    <Box
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            backgroundColor: '#22C55E',
                        }}
                    />
                    <Text size="sm" c="dimmed">
                        Reviewed
                    </Text>
                </Group>
                <Group gap={8}>
                    <Box
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            backgroundColor: '#EF4444',
                        }}
                    />
                    <Text size="sm" c="dimmed">
                        Missed
                    </Text>
                </Group>
                <Group gap={8}>
                    <Box
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            backgroundColor: '#E5E7EB',
                        }}
                    />
                    <Text size="sm" c="dimmed">
                        No data
                    </Text>
                </Group>
            </Group>
        </Box>
    );
}

export default ActivityGrid;
